import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  IconButton,
  InputAdornment,
  Container,
  CircularProgress,
  Link,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n';

type AuthMode = 'login' | 'reset';

const LoginPage: React.FC = () => {
  const [mode, setMode] = useState<AuthMode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, resetPassword } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const getErrorMessage = (code: string): string => {
    switch (code) {
      case 'auth/email-already-in-use': return t.auth.errors.emailInUse;
      case 'auth/user-not-found': return t.auth.errors.userNotFound;
      case 'auth/wrong-password': return t.auth.errors.wrongPassword;
      case 'auth/invalid-email': return t.auth.errors.invalidEmail;
      case 'auth/weak-password': return t.auth.errors.weakPassword;
      case 'auth/invalid-credential': return t.auth.errors.wrongPassword;
      case 'auth/unauthorized-domain': return 'This domain is not authorized for Firebase Authentication. Please contact support.';
      case 'auth/too-many-requests': return 'Too many failed attempts. Please wait a few minutes and try again.';
      case 'auth/network-request-failed': return 'Network error. Please check your connection and try again.';
      case 'auth/user-disabled': return 'This account has been disabled. Please contact support.';
      default: return `${t.auth.errors.generic} (${code || 'unknown'})`;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!email.trim()) { setError(t.auth.errors.invalidEmail); return; }

    if (mode === 'reset') {
      setLoading(true);
      try {
        await resetPassword(email);
        setSuccess(t.auth.resetSuccess);
      } catch (err: any) {
        setError(getErrorMessage(err?.code || ''));
      }
      setLoading(false);
      return;
    }

    if (!password || password.length < 6) { setError(t.auth.errors.weakPassword); return; }

    setLoading(true);
    try {
      await login(email, password);
      navigate('/app');
    } catch (err: any) {
      setError(getErrorMessage(err?.code || ''));
    }
    setLoading(false);
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      backgroundColor: 'rgba(15, 18, 40, 0.8)',
      color: '#E2E8F0',
      fontSize: '0.9rem',
      '& fieldset': {
        borderColor: 'rgba(255,255,255,0.1)',
        borderWidth: 1,
        transition: 'border-color 0.2s ease',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(139,92,246,0.5)',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#8B5CF6',
        borderWidth: 2,
        boxShadow: '0 0 0 3px rgba(139,92,246,0.1)',
      },
      '& input': {
        padding: '14px 16px',
        '&::placeholder': { color: 'rgba(255,255,255,0.25)' },
      },
      '& input:-webkit-autofill': {
        WebkitBoxShadow: '0 0 0 30px #0F1228 inset !important',
        WebkitTextFillColor: '#E2E8F0 !important',
        caretColor: '#E2E8F0',
      },
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(255,255,255,0.4)',
      fontSize: '0.85rem',
      '&.Mui-focused': { color: '#A78BFA' },
    },
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(165deg, #0A0E27 0%, #111633 40%, #1A1F3A 100%)',
        position: 'relative',
        overflow: 'hidden',
        px: 2,
      }}
    >
      {/* Ambient glow */}
      <Box sx={{ position: 'absolute', top: '-30%', right: '-15%', width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(139,92,246,0.1) 0%, transparent 70%)', filter: 'blur(80px)' }} />
      <Box sx={{ position: 'absolute', bottom: '-20%', left: '-10%', width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(59,130,246,0.08) 0%, transparent 70%)', filter: 'blur(60px)' }} />

      <Container maxWidth="xs" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Back to Home */}
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/')}
          sx={{ color: 'rgba(255,255,255,0.5)', mb: 3, borderRadius: 1.5, '&:hover': { color: '#fff' } }}
        >
          Home
        </Button>

        <Box
          sx={{
            p: { xs: 3, sm: 5 },
            borderRadius: 2,
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          {/* Logo */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Box component="img" src="/image.png" alt="KrElixir" sx={{ height: 40, filter: 'brightness(0) invert(1)' }} />
          </Box>

          {/* Title */}
          <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, textAlign: 'center', mb: 0.5 }}>
            {mode === 'login' ? t.auth.loginTitle : t.auth.resetTitle}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', mb: 4 }}>
            {mode === 'login' ? t.auth.loginSubtitle : t.auth.resetSubtitle}
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 1.5 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 1.5 }}>{success}</Alert>}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth label={t.auth.email} type="email" value={email}
              onChange={(e) => setEmail(e.target.value)} sx={{ ...fieldSx, mb: 2.5 }}
            />

            {mode === 'login' && (
              <TextField
                fullWidth label={t.auth.password} type={showPassword ? 'text' : 'password'}
                value={password} onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ ...fieldSx, mb: 1.5 }}
              />
            )}

            {mode === 'login' && (
              <Box sx={{ textAlign: 'right', mb: 3 }}>
                <Link
                  component="button" type="button" variant="body2"
                  onClick={() => { setMode('reset'); setError(''); setSuccess(''); }}
                  sx={{ color: '#A78BFA', textDecoration: 'none', fontSize: '0.8rem', '&:hover': { textDecoration: 'underline' } }}
                >
                  {t.auth.forgotPassword}
                </Link>
              </Box>
            )}

            <Button
              fullWidth type="submit" size="large" disabled={loading}
              sx={{
                py: 1.5, fontWeight: 700, color: '#fff', borderRadius: 1.5,
                background: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
                boxShadow: '0 4px 20px rgba(139,92,246,0.35)',
                '&:hover': { background: 'linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%)' },
                '&:disabled': { opacity: 0.7 },
              }}
            >
              {loading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> :
                mode === 'login' ? t.auth.loginButton : t.auth.resetButton}
            </Button>
          </Box>

          {/* Back to login (from reset mode) */}
          {mode === 'reset' && (
            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Link
                component="button" variant="body2"
                onClick={() => { setMode('login'); setError(''); setSuccess(''); }}
                sx={{ color: '#A78BFA', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                {t.auth.backToLogin}
              </Link>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default LoginPage;
